import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getRewardAmount } from "@/services/offers";
import { getParentProfile } from "@/services/parent";
import { motion } from "framer-motion";
import {
    Gift,
    Share2,
    Coins,
    Users,
    IndianRupee,
    Copy,
    CheckCircle2,
    Sparkles,
    Eye,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

export default function ReferralPage() {
    const userDetails = useCurrentUser();
    const username = userDetails?.username || 'User';
    const [copied, setCopied] = useState(false);
    const [referralCode, setReferralCode] = useState<string>('');
    const [rewardBalance, setRewardBalance] =
        useState(0);
    const [showOffersModal, setShowOffersModal] =
        useState(false);

    const [offers, setOffers] =
        useState<any[]>([]);

    const [loadingRewards, setLoadingRewards] =
        useState(false);

    const loadRewardsAndOffers =
        useCallback(async () => {

            if (!username || username === "User")
                return;

            try {

                setLoadingRewards(true);

                const response = await getRewardAmount(username);

                if (
                    response.status === 200 &&
                    response.data
                ) {

                    const data =
                        Array.isArray(response.data)
                            ? response.data[0]
                            : response.data;

                    setRewardBalance(
                        Number(
                            data?.Reward_Balance || 0
                        )
                    );

                    setOffers(
                        Array.isArray(data?.Offers)
                            ? data.Offers
                            : []
                    );
                }

            } catch (error) {

                console.log(error);

            } finally {

                setLoadingRewards(false);
            }

        }, [username]);

    const APP_URL =
        import.meta.env.VITE_APP_URL || window.location.origin;

    const referralLink = `${APP_URL}/#/register?ref=${referralCode}`;

    const loadProfile = useCallback(async () => {
        if (!username || username === 'User') return;
        try {
            const response = await getParentProfile(username);
            if (response.status === 200 && response.data) {
                const data = Array.isArray(response.data) ? (response.data as any[])[0] : response.data;
                setReferralCode(data?.Referral_Code || '');
            }
        } catch (error) {
            // console.error('Failed to load profile:', error);
        } finally {
            // setProfileLoading(false);
        }
    }, [username]);

    useEffect(() => {
        loadProfile();
        loadRewardsAndOffers();
    }, [
        loadProfile,
        loadRewardsAndOffers,
    ]);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(referralCode);
        setCopied(true);

        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    const handleShare = async () => {
        const shareText =
            `Join Mera Lunch Box using my referral code ${referralCode} and earn rewards!\n\n${referralLink}`;

        try {
            if (
                navigator.share &&
                window.isSecureContext
            ) {
                await navigator.share({
                    title:
                        "Mera Lunch Box Referral",
                    text: shareText,
                    url: referralLink,
                });

                return;
            }

            // Fallback → copy link
            await navigator.clipboard.writeText(
                referralLink
            );

            toast.success(
                "Referral link copied successfully!"
            );
        } catch (error: any) {
            if (
                error?.name ===
                "AbortError"
            ) {
                return;
            }

            console.log(error);

            try {
                await navigator.clipboard.writeText(
                    referralLink
                );

                toast.success(
                    "Referral link copied successfully!"
                );
            } catch {
                toast.error(
                    "Unable to share referral link"
                );
            }
        }
    };

    const handleCopyLink = async () => {
        const shareData = {
            title: "Mera Lunch Box Referral",
            text: `Join Mera Lunch Box using my referral code ${referralCode} and earn rewards!`,
            url: referralLink,
        };

        try {
            // Mobile native share
            if (navigator.share) {
                await navigator.share(shareData);

                return;
            }

            // Fallback copy
            await navigator.clipboard.writeText(
                referralLink
            );

            // setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 2000);

            toast.success(
                "Referral link copied successfully!"
            );
        } catch (err) {
            console.log(err);

            toast.error(
                "Failed to share referral link"
            );
        }
    };

    return (
        <div className="min-h-screen bg-[#f7f8f5] overflow-hidden">
            {/* HERO SECTION */}
            <div className="my-4 grid md:grid-cols-2 gap-5">

                {/* Reward Balance */}
                <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">

                    <div className="flex items-center justify-between">

                        <div>

                            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                                Reward Balance
                            </p>

                            <div className="flex items-center gap-2 mt-3">

                                <Coins className="w-7 h-7 text-[#fdec29] fill-[#fdec29]" />

                                <h2 className="text-4xl font-black text-[#333A45]">
                                    ₹{rewardBalance}
                                </h2>

                            </div>

                            <p className="text-sm text-[#535c6b] mt-2">
                                Available for subscription discounts
                            </p>

                        </div>

                        <div className="w-16 h-16 rounded-2xl bg-[#fdec29]/20 flex items-center justify-center">
                            <Gift className="w-8 h-8 text-[#333A45]" />
                        </div>

                    </div>

                </div>

                {/* Available Offers */}
                {/* Offers Summary */}
                <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">

                    <div className="flex items-center justify-between">

                        <div>

                            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                                Available Offers
                            </p>

                            <h2 className="text-4xl font-black text-[#333A45] mt-2">
                                {offers.length}
                            </h2>

                            <p className="text-sm text-[#535c6b] mt-2">
                                Active discount offers available
                            </p>

                        </div>

                        <button
                            onClick={() =>
                                setShowOffersModal(true)
                            }
                            className="w-16 h-16 rounded-2xl bg-green-100 hover:bg-green-200 transition-all flex items-center justify-center shadow-md"
                        >
                            <Eye className="w-8 h-8 text-green-600" />
                        </button>

                    </div>
                    {offers.length > 0 && (
                        <div className="mt-5 flex flex-wrap gap-2">
                            {offers.slice(0, 3).map(
                                (
                                    offer: any,
                                    index: number
                                ) => (
                                    <div
                                        key={index}
                                        className="bg-green-50 text-green-700 border border-green-100 px-3 py-1 rounded-full text-sm font-semibold"
                                    >
                                        {offer.Discount_Percentage}% OFF
                                    </div>
                                )
                            )}

                            {offers.length > 3 && (
                                <div className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-semibold">
                                    +{offers.length - 3} more
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <section className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#535c6b] via-[#333A45] to-[#1f2937]" />

                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                    <div className="absolute top-10 left-10 w-72 h-72 bg-[#fdec29] rounded-full blur-3xl" />
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#a1ca41] rounded-full blur-3xl" />
                </div>

                <div className="relative max-w-full mx-auto px-5 lg:px-10 py-10 lg:py-16">
                    <div className="grid lg:grid-cols-2 gap-10 items-center">
                        {/* LEFT CONTENT */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-4 py-2 rounded-full text-sm backdrop-blur-md mb-5">
                                <Sparkles className="w-4 h-4 text-[#fdec29]" />
                                Exclusive Referral Rewards
                            </div>

                            <h1 className="text-4xl lg:text-6xl font-black leading-tight text-white">
                                Refer Friends
                                <span className="block text-[#fdec29]">& Earn Rewards</span>
                            </h1>

                            <p className="text-white/80 text-lg lg:text-xl mt-6 leading-relaxed max-w-xl">
                                Invite your friends to join{" "}
                                <span className="font-bold text-[#fdec29]">
                                    Mera Lunch Box
                                </span>{" "}
                                and earn exciting rewards after their first successful booking.
                            </p>

                            {/* REWARD CARD */}
                            <div className="mt-8 bg-white rounded-3xl p-6 shadow-2xl max-w-lg">
                                <div className="flex items-center justify-between flex-wrap gap-5">
                                    <div>
                                        <p className="text-gray-500 text-sm font-medium">
                                            PER REFERRAL
                                        </p>

                                        <div className="flex items-center gap-2 mt-2">
                                            <Coins className="text-[#fdec29] fill-[#fdec29]" />
                                            <h2 className="text-5xl font-black text-[#333A45]">
                                                100 Coins
                                            </h2>
                                        </div>

                                        <p className="text-[#535c6b] font-semibold mt-1">
                                            100 Coins = ₹100 Reward
                                        </p>
                                    </div>

                                    <div className="bg-[#f7f8f5] rounded-2xl p-4">
                                        <div className="flex items-center gap-2 text-[#535c6b] font-semibold">
                                            <IndianRupee className="w-5 h-5" />
                                            1 Coin = ₹1
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 bg-gradient-to-r from-[#a1ca41] to-[#8BB535] rounded-2xl p-4 text-white">
                                    <div className="flex items-center gap-3">
                                        <Gift className="w-6 h-6" />
                                        <div>
                                            <h3 className="font-bold text-lg">
                                                Redeem on Subscription Payment
                                            </h3>
                                            <p className="text-sm text-white/90">
                                                More referrals = More savings
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* REFERRAL CODE */}
                            <div className="mt-8">
                                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
                                    <div>
                                        <p className="text-white/70 text-sm">Your Referral Code</p>
                                        <h3 className="text-white text-2xl font-bold tracking-wider">
                                            {referralCode}
                                        </h3>
                                    </div>

                                    <button
                                        onClick={handleCopy}
                                        className="bg-[#fdec29] hover:bg-[#e8d620] transition-all duration-300 text-[#333A45] font-bold px-5 py-3 rounded-xl flex items-center gap-2 shadow-lg"
                                    >
                                        {copied ? (
                                            <>
                                                <CheckCircle2 className="w-5 h-5" />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-5 h-5" />
                                                Copy Code
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* BUTTONS */}
                            <div className="flex flex-row gap-4 mt-8 justify-center">
                                <div className="flex flex-wrap gap-4 mt-8">
                                    <button onClick={handleShare} className="bg-[#fdec29] hover:bg-[#e8d620] transition-all duration-300 text-[#333A45] px-7 py-4 rounded-2xl font-bold shadow-xl flex items-center gap-3">
                                        <Share2 className="w-5 h-5" />
                                        Refer a Friend
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-4 mt-8">
                                    <button onClick={handleCopyLink} className="bg-[#fdec29] hover:bg-[#e8d620] transition-all duration-300 text-[#333A45] px-7 py-4 rounded-2xl font-bold shadow-xl flex items-center gap-3">
                                        <Share2 className="w-5 h-5" />
                                        Copy Link
                                    </button>
                                </div>
                            </div>
                        </motion.div>

                        {/* RIGHT VISUAL */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="relative mx-auto w-full max-w-md">
                                {/* Glow */}
                                <div className="absolute inset-0 bg-[#fdec29]/30 blur-3xl rounded-full" />

                                {/* PHONE CARD */}
                                <div className="relative bg-white rounded-[40px] shadow-2xl p-4 border-[10px] border-black">
                                    <div className="bg-[#f7f8f5] rounded-[28px] p-5">
                                        <div className="flex items-center justify-center">
                                            <div className="bg-[#535c6b] text-white text-sm px-4 py-2 rounded-full">
                                                Mera Lunch Box
                                            </div>
                                        </div>

                                        <div className="mt-8 text-center">
                                            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#fdec29] to-[#a1ca41] flex items-center justify-center shadow-xl">
                                                <Users className="w-12 h-12 text-[#333A45]" />
                                            </div>

                                            <h3 className="text-3xl font-black text-[#333A45] mt-6">
                                                REFER
                                            </h3>

                                            <p className="text-[#535c6b] font-medium mt-2">
                                                Invite your friends and earn rewards instantly
                                            </p>

                                            <div className="mt-8 bg-[#fdec29] rounded-2xl py-4 px-6 shadow-lg">
                                                <p className="text-[#333A45] text-lg font-black">
                                                    Earn 100 Coins for every successful referral!
                                                </p>
                                            </div>

                                            <button onClick={handleShare} className="w-full mt-6 bg-[#a1ca41] hover:bg-[#8BB535] transition-all duration-300 text-white font-bold py-4 rounded-2xl shadow-lg">
                                                SHARE NOW
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* FLOATING ELEMENTS */}
                                <div className="absolute -top-5 -right-5 bg-white rounded-2xl p-4 shadow-xl">
                                    <Coins className="w-8 h-8 text-[#fdec29] fill-[#fdec29]" />
                                </div>

                                <div className="absolute -bottom-5 -left-5 bg-[#fdec29] rounded-2xl p-4 shadow-xl">
                                    <Gift className="w-8 h-8 text-[#333A45]" />
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="max-w-full mx-auto px-5 lg:px-10 py-16">
                <div className="text-center">
                    <h2 className="text-4xl font-black text-[#333A45]">
                        How It Works
                    </h2>

                    <p className="text-[#535c6b] mt-4 text-lg">
                        Invite. Earn. Redeem.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mt-12">
                    {[
                        {
                            icon: Share2,
                            title: "Share Referral",
                            desc: "Send your referral code to your friends and family.",
                        },
                        {
                            icon: Users,
                            title: "Friend Registers",
                            desc: "Your friend completes their first successful subscription.",
                        },
                        {
                            icon: Coins,
                            title: "Earn Rewards",
                            desc: "Get 100 Coins instantly in your wallet.",
                        },
                    ].map((item, index) => (
                        <motion.div
                            whileHover={{ y: -5 }}
                            key={index}
                            className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-[#fdec29]/20 flex items-center justify-center">
                                <item.icon className="w-8 h-8 text-[#333A45]" />
                            </div>

                            <h3 className="text-2xl font-bold text-[#333A45] mt-6">
                                {item.title}
                            </h3>

                            <p className="text-[#535c6b] mt-3 leading-relaxed">
                                {item.desc}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-[#333A45] text-white py-8 mt-10">
                <div className="max-w-full mx-auto px-5 lg:px-10 text-center">
                    <h3 className="text-2xl font-black text-[#fdec29]">
                        Thank You For Being Part Of Mera Lunch Box Family ❤️
                    </h3>

                    <p className="text-white/70 mt-3">
                        More referrals. More rewards. More savings.
                    </p>
                </div>
            </footer>
            {showOffersModal && (

                <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">

                    <div className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden shadow-2xl">

                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b">

                            <div>

                                <h2 className="text-2xl font-black text-[#333A45]">
                                    Available Offers
                                </h2>

                                <p className="text-sm text-gray-500 mt-1">
                                    Exclusive rewards & discounts
                                </p>

                            </div>

                            <button
                                onClick={() =>
                                    setShowOffersModal(false)
                                }
                                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-all text-xl"
                            >
                                ×
                            </button>

                        </div>

                        {/* Offers List */}
                        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">

                            {loadingRewards ? (

                                <div className="text-center text-gray-400 py-10">
                                    Loading offers...
                                </div>

                            ) : offers.length > 0 ? (

                                offers.map(
                                    (
                                        offer: any,
                                        index: number
                                    ) => (

                                        <div
                                            key={index}
                                            className="relative overflow-hidden border border-green-100 bg-gradient-to-r from-green-50 to-white rounded-3xl p-5 shadow-sm"
                                        >

                                            <div className="absolute top-0 right-0 bg-green-600 text-white text-xs font-bold px-4 py-1 rounded-bl-2xl">
                                                ACTIVE
                                            </div>

                                            <div className="flex items-start justify-between gap-4">

                                                <div>

                                                    <div className="flex items-center gap-3">

                                                        <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
                                                            <Sparkles className="w-7 h-7 text-green-600" />
                                                        </div>

                                                        <div>

                                                            <h3 className="text-xl font-black text-[#333A45]">
                                                                {offer.Discount_Percentage}% OFF
                                                            </h3>

                                                            <p className="text-sm text-gray-500 mt-1">
                                                                Save instantly on subscription
                                                            </p>

                                                        </div>

                                                    </div>

                                                    <div className="mt-4 flex items-center gap-2">

                                                        <div className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold">
                                                            ₹{offer.Discount_Amount}
                                                        </div>

                                                        <div className="text-sm text-gray-500">
                                                            Discount Amount
                                                        </div>

                                                    </div>

                                                </div>

                                            </div>

                                        </div>
                                    )
                                )

                            ) : (

                                <div className="text-center py-10">

                                    <Sparkles className="w-14 h-14 text-gray-300 mx-auto" />

                                    <p className="text-gray-400 mt-4">
                                        No offers available
                                    </p>

                                </div>

                            )}

                        </div>

                    </div>

                </div>

            )}
        </div>


    );
}